import { useState, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePersons, useDocuments, useFlights, useEmails } from '../hooks/useApi';
import type { ViewType } from '../party';
import { resolveImageUrl, type Person, type Document, type Flight } from '../api';

interface EntityListProps {
  view: ViewType;
  onSelect: (type: string, id: string) => void;
  selectedId: string | null;
  filters: Record<string, any>;
  onUpdateFilters: (f: Record<string, any>) => void;
}

const CATEGORIES = [
  'politician', 'business', 'royalty', 'celebrity', 'associate',
  'legal', 'academic', 'socialite', 'military-intelligence', 'other',
];

const CATEGORY_COLORS: Record<string, string> = {
  politician: '#ED4245',
  business: '#3BA55C',
  royalty: '#FAA61A',
  celebrity: '#5865F2',
  associate: '#EB459E',
  legal: '#9B59B6',
  academic: '#1ABC9C',
  socialite: '#E67E22',
  'military-intelligence': '#2C3E50',
  other: '#95A5A6',
};

export default function EntityList({ view, onSelect, selectedId, filters, onUpdateFilters }: EntityListProps) {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const parentRef = useRef<HTMLDivElement>(null);

  const personsQuery = usePersons(
    view === 'persons' || view === 'graph'
      ? { q: search || undefined, category: categoryFilter || undefined, per_page: 100, sort: 'connections' }
      : undefined
  );

  const docsQuery = useDocuments(
    view === 'documents'
      ? { q: search || undefined, per_page: 50, sort: 'newest' }
      : undefined
  );

  const flightsQuery = useFlights(
    view === 'flights'
      ? { passenger: search || undefined, per_page: 50 }
      : undefined
  );

  interface ListItem {
    id: string;
    type: string;
    title: string;
    subtitle: string;
    badge?: string;
    stats: string;
    image?: string | null;
    color: string;
  }

  const items: ListItem[] = useMemo(() => {
    if (view === 'persons' || view === 'graph') {
      return (personsQuery.data?.data || []).map((p: Person) => ({
        id: p.slug,
        type: 'person',
        title: p.name,
        subtitle: p.category,
        badge: p.black_book_entry ? 'BB' : undefined,
        stats: `${p.stats.documents} docs \u00B7 ${p.stats.connections} links`,
        image: resolveImageUrl(p.image_url),
        color: CATEGORY_COLORS[p.category] || '#95A5A6',
      }));
    }
    if (view === 'documents') {
      return (docsQuery.data?.data || []).map((d: Document) => ({
        id: d.id,
        type: 'document',
        title: d.title,
        subtitle: `${d.source} \u00B7 ${d.category}`,
        badge: d.page_count ? `${d.page_count}p` : undefined,
        stats: d.date || 'No date',
        color: '#5865F2',
      }));
    }
    if (view === 'flights') {
      return (flightsQuery.data?.data || []).map((f: Flight) => ({
        id: f.id,
        type: 'flight',
        title: `${f.origin} \u2192 ${f.destination}`,
        subtitle: f.aircraft,
        stats: `${f.date} \u00B7 ${f.passenger_count} pax`,
        color: '#FAA61A',
      }));
    }
    return [];
  }, [view, personsQuery.data, docsQuery.data, flightsQuery.data]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 64,
    overscan: 10,
  });

  const isLoading = personsQuery.isLoading || docsQuery.isLoading || flightsQuery.isLoading;

  return (
    <div className="entity-list">
      <div className="entity-list-header">
        <input
          type="text"
          className="entity-search"
          placeholder={`Filter ${view}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {(view === 'persons' || view === 'graph') && (
          <div className="category-pills">
            <button
              className={`pill ${!categoryFilter ? 'active' : ''}`}
              onClick={() => setCategoryFilter('')}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`pill ${categoryFilter === cat ? 'active' : ''}`}
                onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}
                style={{ '--pill-color': CATEGORY_COLORS[cat] } as React.CSSProperties}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="entity-list-scroll" ref={parentRef}>
        {isLoading ? (
          <div className="entity-list-loading">Loading...</div>
        ) : items.length === 0 ? (
          <div className="entity-list-empty">No results</div>
        ) : (
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const item = items[vItem.index];
              return (
                <button
                  key={item.id}
                  className={`entity-item ${selectedId === item.id ? 'selected' : ''}`}
                  style={{
                    position: 'absolute',
                    top: vItem.start,
                    height: vItem.size,
                    width: '100%',
                  }}
                  onClick={() => onSelect(item.type, item.id)}
                >
                  <div className="entity-color-bar" style={{ backgroundColor: item.color }} />
                  {'image' in item && item.image && (
                    <img className="entity-thumb" src={item.image} alt="" loading="lazy" />
                  )}
                  <div className="entity-info">
                    <div className="entity-title">
                      {item.title}
                      {item.badge && <span className="entity-badge">{item.badge}</span>}
                    </div>
                    <div className="entity-subtitle">{item.subtitle}</div>
                    <div className="entity-stats">{item.stats}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
